import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { userApi } from '../utils/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  login: (username: string, password: string) => Promise<boolean>;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  fetchUser: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setIsLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (username: string, password: string) => {
        try {
          set({ isLoading: true });
          const response = await userApi.login({ username, password });
          
          if (response.success && response.data) {
            localStorage.setItem('token', response.data.token);
            set({
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
            });
            await get().fetchUser();
            return true;
          }
          set({ isLoading: false });
          return false;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (username: string, email: string, password: string) => {
        try {
          set({ isLoading: true });
          const response = await userApi.register({ username, email, password });
          
          if (response.success) {
            // 注册成功，但不自动登录，用户需要验证邮箱后才能登录
            set({ isLoading: false });
            return true;
          }
          set({ isLoading: false });
          return false;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      fetchUser: async (): Promise<void> => {
        try {
          const response = await userApi.getCurrentUser();
          if (response.success && response.data) {
            set({ user: response.data, isAuthenticated: true });
          }
        } catch (error) {
          console.error('Fetch user error:', error);
          // 即使获取用户信息失败，也不要重置登录状态
          // 因为用户已经通过登录验证，只是获取详细信息失败
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },

      setIsLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
